import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { RutField, fillEmpty } from '../components/RutField';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  User,
  Plus,
  Minus,
  ChevronRight,
  ChevronLeft,
  Check,
  AlertTriangle,
  FileText,
  Download,
  Copy,
  ArrowLeft,
  Briefcase,
  MapPin,
  DollarSign,
  Calendar,
  Shield,
  Info,
  Sparkles,
  CheckCircle,
  XCircle,
  Percent,
  Hash,
  Building,
  Globe,
  Phone,
  Mail,
  FileCheck
} from 'lucide-react';

// RUT Validation Functions
const cleanRut = (rut) => {
  if (!rut) return '';
  return rut.replace(/[.-]/g, '').toUpperCase();
};

const formatRut = (rut) => {
  if (!rut) return '';
  let cleaned = cleanRut(rut).replace(/[^0-9K]/gi, '');
  if (cleaned.length < 2) return cleaned;
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  let formatted = '';
  for (let i = body.length - 1, count = 0; i >= 0; i--, count++) {
    if (count > 0 && count % 3 === 0) formatted = '.' + formatted;
    formatted = body[i] + formatted;
  }
  return `${formatted}-${dv}`;
};

const calculateDV = (rutBody) => {
  const body = rutBody.replace(/\D/g, '');
  if (!body) return '';
  let sum = 0, mul = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * mul;
    mul = mul === 7 ? 2 : mul + 1;
  }
  const remainder = sum % 11;
  const digit = 11 - remainder;
  if (digit === 11) return '0';
  if (digit === 10) return 'K';
  return digit.toString();
};

const validateRut = (rut) => {
  if (!rut) return { isValid: false, error: 'RUT requerido' };
  const cleaned = cleanRut(rut);
  if (cleaned.length < 8) return { isValid: false, error: 'RUT muy corto' };
  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);
  if (!/^\d+$/.test(body)) return { isValid: false, error: 'RUT inválido' };
  const expectedDv = calculateDV(body);
  if (dv !== expectedDv) return { isValid: false, error: `Dígito incorrecto (esperado: ${expectedDv})` };
  return { isValid: true };
};

// Company Types Configuration
const COMPANY_TYPES = {
  spa: {
    id: 'spa',
    name: 'Sociedad por Acciones (SpA)',
    shortName: 'SpA',
    minPartners: 1,
    maxPartners: 500,
    description: 'Ideal para startups y empresas que buscan inversión',
    liability: 'limitada',
    features: ['Un solo accionista posible', 'Máxima flexibilidad', 'Fácil transferencia de acciones'],
    color: 'purple'
  },
  ltda: {
    id: 'ltda',
    name: 'Sociedad de Responsabilidad Limitada',
    shortName: 'Ltda',
    minPartners: 2,
    maxPartners: 50,
    description: 'Para PyMEs y negocios familiares',
    liability: 'limitada',
    features: ['Estructura simple', 'Bajo costo', 'Ideal para negocios familiares'],
    color: 'green'
  },
  eirl: {
    id: 'eirl',
    name: 'Empresa Individual de Resp. Limitada',
    shortName: 'EIRL',
    minPartners: 1,
    maxPartners: 1,
    description: 'Para emprendedores individuales',
    liability: 'limitada',
    features: ['Un solo titular', 'Patrimonio separado', 'Simple de administrar'],
    color: 'blue'
  },
  sa_cerrada: {
    id: 'sa_cerrada',
    name: 'Sociedad Anónima Cerrada',
    shortName: 'S.A. Cerrada',
    minPartners: 2,
    maxPartners: 500,
    description: 'Para empresas medianas con estructura formal',
    liability: 'limitada',
    features: ['Directorio formal', 'Acciones transferibles', 'Mayor credibilidad'],
    color: 'amber'
  }
};

// Giro suggestions based on category
const GIROS_SUGERIDOS = {
  tecnologia: [
    'Desarrollo de software y aplicaciones',
    'Servicios de consultoría informática',
    'Comercio electrónico',
    'Servicios de hosting y cloud',
    'Desarrollo de videojuegos'
  ],
  comercio: [
    'Compraventa de productos al por menor',
    'Compraventa de productos al por mayor',
    'Importación y exportación de mercaderías',
    'Comercio de alimentos y bebidas',
    'Comercio de vestuario y accesorios'
  ],
  servicios: [
    'Servicios profesionales de consultoría',
    'Servicios de marketing y publicidad',
    'Servicios de contabilidad y auditoría',
    'Servicios legales',
    'Servicios de recursos humanos'
  ],
  construccion: [
    'Construcción de obras civiles',
    'Remodelación y ampliación de inmuebles',
    'Servicios de arquitectura e ingeniería',
    'Instalaciones eléctricas y sanitarias',
    'Carpintería y mueblería'
  ],
  salud: [
    'Servicios médicos y de salud',
    'Clínica dental',
    'Servicios de kinesiología y rehabilitación',
    'Laboratorio clínico',
    'Farmacia'
  ],
  educacion: [
    'Servicios de capacitación y formación',
    'Academia de idiomas',
    'Educación preescolar',
    'Clases particulares',
    'Cursos online'
  ],
  gastronomia: [
    'Restaurante y cafetería',
    'Servicio de catering y banquetería',
    'Food truck',
    'Panadería y pastelería',
    'Delivery de comida'
  ],
  transporte: [
    'Transporte de carga terrestre',
    'Servicios de mudanza',
    'Transporte de pasajeros',
    'Mensajería y courier',
    'Arriendo de vehículos'
  ]
};

// Chilean regions and communes
const REGIONES_CHILE = [
  { id: 'RM', name: 'Región Metropolitana', comunas: ['Santiago', 'Providencia', 'Las Condes', 'Ñuñoa', 'Vitacura', 'La Florida', 'Maipú', 'Puente Alto', 'San Bernardo', 'Peñalolén'] },
  { id: 'V', name: 'Valparaíso', comunas: ['Valparaíso', 'Viña del Mar', 'Quilpué', 'Villa Alemana', 'San Antonio'] },
  { id: 'VIII', name: 'Biobío', comunas: ['Concepción', 'Talcahuano', 'Chillán', 'Los Ángeles'] },
  { id: 'IX', name: 'La Araucanía', comunas: ['Temuco', 'Padre Las Casas', 'Villarrica'] },
  { id: 'X', name: 'Los Lagos', comunas: ['Puerto Montt', 'Osorno', 'Castro'] },
  { id: 'II', name: 'Antofagasta', comunas: ['Antofagasta', 'Calama', 'Tocopilla'] },
  { id: 'IV', name: 'Coquimbo', comunas: ['La Serena', 'Coquimbo', 'Ovalle'] },
  { id: 'VI', name: "O'Higgins", comunas: ['Rancagua', 'San Fernando', 'Rengo'] },
  { id: 'VII', name: 'Maule', comunas: ['Talca', 'Curicó', 'Linares'] }
];

// RUT Input Component
const RutInput = ({ value, onChange, label, required, disabled }) => {
  const [displayValue, setDisplayValue] = useState('');
  const [validation, setValidation] = useState({ isValid: false, error: null });
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (value) setDisplayValue(formatRut(value));
  }, []);

  const handleChange = (e) => {
    let inputValue = e.target.value.replace(/[^0-9kK.-]/g, '');
    const formatted = formatRut(inputValue);
    setDisplayValue(formatted);
    const cleanValue = cleanRut(formatted);
    if (onChange) onChange(cleanValue);
    if (!cleanValue) {
      setValidation({ isValid: false, error: required ? 'RUT requerido' : null });
      return;
    }
    setValidation(validateRut(cleanValue));
  };

  const showError = touched && validation.error && !validation.isValid;
  const showSuccess = touched && validation.isValid;

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium text-slate-300">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={() => setTouched(true)}
          disabled={disabled}
          placeholder="12.345.678-9"
          maxLength={12}
          className={`w-full px-4 py-2.5 bg-slate-800 border rounded-lg text-white focus:outline-none pr-12 transition-colors ${
            disabled ? 'opacity-50 cursor-not-allowed' :
            showSuccess ? 'border-emerald-500/50 focus:border-emerald-500' :
            showError ? 'border-red-500/50 focus:border-red-500' :
            'border-slate-700 focus:border-purple-500'
          }`}
        />
        {touched && displayValue && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {showSuccess && <CheckCircle className="w-5 h-5 text-emerald-400" />}
            {showError && <XCircle className="w-5 h-5 text-red-400" />}
          </div>
        )}
      </div>
      {showError && <p className="text-xs text-red-400">{validation.error}</p>}
    </div>
  );
};

// Partner Form Component
const PartnerForm = ({ partner, index, onChange, onRemove, canRemove, companyType, totalPartners }) => {
  const isEIRL = companyType === 'eirl';
  const partnerLabel = isEIRL ? 'Titular' : `Socio ${index + 1}`;

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-white flex items-center gap-2">
          <User className="w-5 h-5 text-purple-400" />
          {partnerLabel}
        </h4>
        {canRemove && (
          <button
            onClick={onRemove}
            className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
          >
            <Minus className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-300">
            Nombre Completo <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={partner.nombre || ''}
            onChange={(e) => onChange({ ...partner, nombre: e.target.value })}
            placeholder="Juan Andrés Pérez González"
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-300">RUT <span className="text-red-400">*</span></label>
          <RutField
            testid={`partner-rut-${partner.rut ? 'filled' : 'empty'}`}
            value={partner.rut || ''}
            onChange={(val) => onChange({ ...partner, rut: val })}
            onLookup={(d) => onChange(fillEmpty({ ...partner }, { nombre: d.name }))}
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-300">
            Nacionalidad <span className="text-red-400">*</span>
          </label>
          <select
            value={partner.nacionalidad || 'Chilena'}
            onChange={(e) => onChange({ ...partner, nacionalidad: e.target.value })}
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
          >
            <option value="Chilena">Chilena</option>
            <option value="Argentina">Argentina</option>
            <option value="Peruana">Peruana</option>
            <option value="Colombiana">Colombiana</option>
            <option value="Venezolana">Venezolana</option>
            <option value="Otra">Otra</option>
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-300">
            Estado Civil <span className="text-red-400">*</span>
          </label>
          <select
            value={partner.estadoCivil || ''}
            onChange={(e) => onChange({ ...partner, estadoCivil: e.target.value })}
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
          >
            <option value="">Seleccionar...</option>
            <option value="Soltero/a">Soltero/a</option>
            <option value="Casado/a">Casado/a</option>
            <option value="Divorciado/a">Divorciado/a</option>
            <option value="Viudo/a">Viudo/a</option>
            <option value="Conviviente Civil">Conviviente Civil</option>
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-300">
            Profesión u Oficio <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={partner.profesion || ''}
            onChange={(e) => onChange({ ...partner, profesion: e.target.value })}
            placeholder="Ingeniero Comercial"
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-slate-300">
            Correo Electrónico <span className="text-red-400">*</span>
          </label>
          <input
            type="email"
            value={partner.email || ''}
            onChange={(e) => onChange({ ...partner, email: e.target.value })}
            placeholder="correo@ejemplo.cl"
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
          />
        </div>

        <div className="md:col-span-2 space-y-1">
          <label className="text-sm font-medium text-slate-300">
            Domicilio <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={partner.domicilio || ''}
            onChange={(e) => onChange({ ...partner, domicilio: e.target.value })}
            placeholder="Av. Providencia 1234, Depto 567, Providencia"
            className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
          />
        </div>

        {!isEIRL && totalPartners > 1 && (
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Percent className="w-4 h-4" />
              Participación (%) <span className="text-red-400">*</span>
            </label>
            <input
              type="number"
              min="0.01"
              max="100"
              step="0.01"
              value={partner.participacion || ''}
              onChange={(e) => onChange({ ...partner, participacion: parseFloat(e.target.value) || 0 })}
              placeholder="50"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
            />
          </div>
        )}

        {!isEIRL && totalPartners > 1 && (
          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Aporte ($) <span className="text-red-400">*</span>
            </label>
            <input
              type="number"
              min="0"
              value={partner.aporte || ''}
              onChange={(e) => onChange({ ...partner, aporte: parseInt(e.target.value) || 0 })}
              placeholder="1000000"
              className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
const CompanyConstitutionPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(1);
  const [companyType, setCompanyType] = useState(null);
  const [numPartners, setNumPartners] = useState(1);
  const [partners, setPartners] = useState([{ id: 1 }]);
  const [companyData, setCompanyData] = useState({
    nombreEmpresa: '',
    nombreFantasia: '',
    region: '',
    comuna: '',
    direccion: '',
    giroCategoria: '',
    giro: '',
    giroPersonalizado: '',
    capital: '',
    duracion: 'indefinida',
    duracionAnos: '',
    administracion: 'todos',
    administradorNombre: '',
    representanteLegal: 0
  });
  const [generatedDocument, setGeneratedDocument] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check URL params for pre-selected type
  useEffect(() => {
    const tipo = searchParams.get('tipo');
    if (tipo && COMPANY_TYPES[tipo]) {
      handleCompanyTypeSelect(tipo);
      setStep(2); // Skip to partners step
    }
  }, [searchParams]);

  const totalSteps = 5;
  const progress = (step / totalSteps) * 100;

  // Get available communes based on selected region
  const comunasDisponibles = REGIONES_CHILE.find(r => r.id === companyData.region)?.comunas || [];

  // Calculate total participation
  const totalParticipacion = partners.reduce((sum, p) => sum + (p.participacion || 0), 0);
  const totalAportes = partners.reduce((sum, p) => sum + (p.aporte || 0), 0);

  const handleCompanyTypeSelect = (type) => {
    setCompanyType(type);
    const config = COMPANY_TYPES[type];
    if (config.maxPartners === 1) {
      setNumPartners(1);
      setPartners([{ id: 1, participacion: 100 }]);
    } else {
      setNumPartners(config.minPartners);
      setPartners(Array.from({ length: config.minPartners }, (_, i) => ({ 
        id: i + 1,
        participacion: Math.floor(100 / config.minPartners)
      })));
    }
  };

  const handleNumPartnersChange = (num) => {
    const config = COMPANY_TYPES[companyType];
    const validNum = Math.max(config.minPartners, Math.min(config.maxPartners, num));
    setNumPartners(validNum);
    
    if (validNum > partners.length) {
      const newPartners = [...partners];
      for (let i = partners.length; i < validNum; i++) {
        newPartners.push({ id: i + 1, participacion: 0, aporte: 0 });
      }
      setPartners(newPartners);
    } else if (validNum < partners.length) {
      setPartners(partners.slice(0, validNum));
    }
  };

  const handlePartnerChange = (index, data) => {
    const newPartners = [...partners];
    newPartners[index] = { ...newPartners[index], ...data };
    setPartners(newPartners);
  };

  const handleAddPartner = () => {
    const config = COMPANY_TYPES[companyType];
    if (partners.length < config.maxPartners) {
      setPartners([...partners, { id: partners.length + 1, participacion: 0, aporte: 0 }]);
      setNumPartners(partners.length + 1);
    }
  };

  const handleRemovePartner = (index) => {
    const config = COMPANY_TYPES[companyType];
    if (partners.length > config.minPartners) {
      const newPartners = partners.filter((_, i) => i !== index);
      setPartners(newPartners);
      setNumPartners(newPartners.length);
    }
  };

  const distributeEqually = () => {
    const equalShare = Math.floor(100 / partners.length);
    const remainder = 100 - (equalShare * partners.length);
    const newPartners = partners.map((p, i) => ({
      ...p,
      participacion: equalShare + (i === 0 ? remainder : 0)
    }));
    setPartners(newPartners);
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return companyType !== null;
      case 2:
        return partners.every(p => p.nombre && p.rut && validateRut(p.rut).isValid && p.estadoCivil && p.profesion && p.email && p.domicilio);
      case 3:
        return companyData.nombreEmpresa && companyData.region && companyData.comuna && companyData.direccion;
      case 4:
        return companyData.giro && companyData.capital;
      default:
        return true;
    }
  };

  const generateDocument = async () => {
    setGenerating(true);
    try {
      const response = await api.post('/legal-docs/generate', {
        document_type: `constitucion_${companyType}`,
        data: {
          ...companyData,
          socios: partners,
          tipoEmpresa: COMPANY_TYPES[companyType].name,
          fecha: new Date().toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' }),
          ciudad: companyData.comuna
        }
      });
      setGeneratedDocument(response.data);
      setStep(5);
      toast.success('Documento generado exitosamente');
    } catch (error) {
      console.error('Error generating document:', error);
      toast.error('Error al generar el documento');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (generatedDocument?.content) {
      navigator.clipboard.writeText(generatedDocument.content);
      setCopied(true);
      toast.success('Documento copiado');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-purple-900/20">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/escritos-legales" className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5 text-slate-400" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Building2 className="w-6 h-6 text-purple-400" />
                  Constituir Empresa
                </h1>
                <p className="text-sm text-slate-400">Modelo Empresa en un Día</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-slate-400">Paso {step} de {totalSteps}</span>
              <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Step 1: Company Type Selection */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">¿Qué tipo de empresa deseas crear?</h2>
              <p className="text-slate-400">Selecciona el tipo de sociedad que mejor se adapte a tus necesidades</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.values(COMPANY_TYPES).map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleCompanyTypeSelect(type.id)}
                  className={`p-6 rounded-xl border-2 text-left transition-all ${
                    companyType === type.id
                      ? `bg-${type.color}-500/20 border-${type.color}-500`
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`p-3 rounded-lg bg-${type.color}-500/20`}>
                      <Building2 className={`w-6 h-6 text-${type.color}-400`} />
                    </div>
                    {companyType === type.id && (
                      <CheckCircle className="w-6 h-6 text-emerald-400" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{type.shortName}</h3>
                  <p className="text-sm text-slate-400 mb-3">{type.description}</p>
                  <div className="space-y-1">
                    {type.features.map((f, i) => (
                      <p key={i} className="text-xs text-slate-500 flex items-center gap-1">
                        <Check className="w-3 h-3 text-emerald-400" /> {f}
                      </p>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-700">
                    <p className="text-xs text-slate-500">
                      <Users className="w-3 h-3 inline mr-1" />
                      {type.minPartners === type.maxPartners 
                        ? `${type.minPartners} socio` 
                        : `${type.minPartners} a ${type.maxPartners} socios`}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Partners Information */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">
                {COMPANY_TYPES[companyType]?.maxPartners === 1 ? 'Datos del Titular' : 'Datos de los Socios'}
              </h2>
              <p className="text-slate-400">Ingresa la información de cada participante</p>
            </div>

            {/* Number of partners selector (only for multi-partner companies) */}
            {COMPANY_TYPES[companyType]?.maxPartners > 1 && (
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-white">¿Cuántos socios tendrá la empresa?</h4>
                    <p className="text-sm text-slate-400">
                      Mínimo {COMPANY_TYPES[companyType]?.minPartners}, máximo {COMPANY_TYPES[companyType]?.maxPartners}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleNumPartnersChange(numPartners - 1)}
                      disabled={numPartners <= COMPANY_TYPES[companyType]?.minPartners}
                      className="p-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    <span className="text-2xl font-bold text-white w-12 text-center">{numPartners}</span>
                    <button
                      onClick={() => handleNumPartnersChange(numPartners + 1)}
                      disabled={numPartners >= COMPANY_TYPES[companyType]?.maxPartners}
                      className="p-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Participation summary */}
                {partners.length > 1 && (
                  <div className="mt-4 pt-4 border-t border-slate-700">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">Participación total:</span>
                      <span className={totalParticipacion === 100 ? 'text-emerald-400' : 'text-amber-400'}>
                        {totalParticipacion}% {totalParticipacion !== 100 && '(debe ser 100%)'}
                      </span>
                    </div>
                    <button
                      onClick={distributeEqually}
                      className="mt-2 text-sm text-purple-400 hover:text-purple-300"
                    >
                      Distribuir equitativamente
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Partner forms */}
            <div className="space-y-4">
              {partners.map((partner, index) => (
                <PartnerForm
                  key={partner.id}
                  partner={partner}
                  index={index}
                  onChange={(data) => handlePartnerChange(index, data)}
                  onRemove={() => handleRemovePartner(index)}
                  canRemove={partners.length > COMPANY_TYPES[companyType]?.minPartners}
                  companyType={companyType}
                  totalPartners={partners.length}
                />
              ))}
            </div>

            {/* Add partner button */}
            {COMPANY_TYPES[companyType]?.maxPartners > partners.length && (
              <button
                onClick={handleAddPartner}
                className="w-full py-3 border-2 border-dashed border-slate-700 hover:border-purple-500 rounded-xl text-slate-400 hover:text-purple-400 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Agregar otro socio
              </button>
            )}
          </div>
        )}

        {/* Step 3: Company Basic Info */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Información de la Empresa</h2>
              <p className="text-slate-400">Datos básicos de identificación</p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2 space-y-1">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Nombre o Razón Social <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={companyData.nombreEmpresa}
                    onChange={(e) => setCompanyData({ ...companyData, nombreEmpresa: e.target.value })}
                    placeholder="Ej: Innovación Digital Chile"
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  />
                  <p className="text-xs text-slate-500">
                    Se agregará automáticamente el sufijo "{COMPANY_TYPES[companyType]?.shortName}"
                  </p>
                </div>

                <div className="md:col-span-2 space-y-1">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    Nombre de Fantasía (opcional)
                  </label>
                  <input
                    type="text"
                    value={companyData.nombreFantasia}
                    onChange={(e) => setCompanyData({ ...companyData, nombreFantasia: e.target.value })}
                    placeholder="Ej: TechSolutions"
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Globe className="w-4 h-4" />
                    Región <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={companyData.region}
                    onChange={(e) => setCompanyData({ ...companyData, region: e.target.value, comuna: '' })}
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="">Seleccionar región...</option>
                    {REGIONES_CHILE.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Comuna <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={companyData.comuna}
                    onChange={(e) => setCompanyData({ ...companyData, comuna: e.target.value })}
                    disabled={!companyData.region}
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none disabled:opacity-50"
                  >
                    <option value="">Seleccionar comuna...</option>
                    {comunasDisponibles.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="md:col-span-2 space-y-1">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Dirección Comercial <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={companyData.direccion}
                    onChange={(e) => setCompanyData({ ...companyData, direccion: e.target.value })}
                    placeholder="Av. Providencia 1234, Oficina 567"
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Business Activity and Capital */}
        {step === 4 && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Actividad y Capital</h2>
              <p className="text-slate-400">Define el giro comercial y el capital inicial</p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 space-y-5">
              {/* Giro Category */}
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Categoría del Giro <span className="text-red-400">*</span>
                </label>
                <select
                  value={companyData.giroCategoria}
                  onChange={(e) => setCompanyData({ ...companyData, giroCategoria: e.target.value, giro: '' })}
                  className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                >
                  <option value="">Seleccionar categoría...</option>
                  <option value="tecnologia">Tecnología e Informática</option>
                  <option value="comercio">Comercio</option>
                  <option value="servicios">Servicios Profesionales</option>
                  <option value="construccion">Construcción</option>
                  <option value="salud">Salud</option>
                  <option value="educacion">Educación</option>
                  <option value="gastronomia">Gastronomía</option>
                  <option value="transporte">Transporte y Logística</option>
                </select>
              </div>

              {/* Giro Selection */}
              {companyData.giroCategoria && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-300">
                    Giro Comercial <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={companyData.giro}
                    onChange={(e) => setCompanyData({ ...companyData, giro: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="">Seleccionar giro...</option>
                    {GIROS_SUGERIDOS[companyData.giroCategoria]?.map((g, i) => (
                      <option key={i} value={g}>{g}</option>
                    ))}
                    <option value="personalizado">Otro (especificar)</option>
                  </select>
                </div>
              )}

              {companyData.giro === 'personalizado' && (
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-300">
                    Especificar Giro <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={companyData.giroPersonalizado}
                    onChange={(e) => setCompanyData({ ...companyData, giroPersonalizado: e.target.value })}
                    placeholder="Describe detalladamente la actividad comercial..."
                    rows={3}
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none resize-none"
                  />
                </div>
              )}

              {/* Capital */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Capital Social ($) <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={companyData.capital}
                    onChange={(e) => setCompanyData({ ...companyData, capital: e.target.value })}
                    placeholder="1000000"
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  />
                  <p className="text-xs text-slate-500">No existe capital mínimo legal en Chile</p>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Duración de la Sociedad
                  </label>
                  <select
                    value={companyData.duracion}
                    onChange={(e) => setCompanyData({ ...companyData, duracion: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                  >
                    <option value="indefinida">Indefinida</option>
                    <option value="definida">Plazo definido</option>
                  </select>
                </div>

                {companyData.duracion === 'definida' && (
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300">
                      Años de duración
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={companyData.duracionAnos}
                      onChange={(e) => setCompanyData({ ...companyData, duracionAnos: e.target.value })}
                      placeholder="5"
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                )}
              </div>

              {/* Administration */}
              {partners.length > 1 && (
                <>
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Administración
                    </label>
                    <select
                      value={companyData.administracion}
                      onChange={(e) => setCompanyData({ ...companyData, administracion: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                    >
                      <option value="todos">Todos los socios conjuntamente</option>
                      <option value="cualquiera">Cualquiera de los socios indistintamente</option>
                      <option value="uno">Un socio administrador</option>
                    </select>
                  </div>

                  {companyData.administracion === 'uno' && (
                    <div className="space-y-1">
                      <label className="text-sm font-medium text-slate-300">
                        Socio Administrador
                      </label>
                      <select
                        value={companyData.representanteLegal}
                        onChange={(e) => setCompanyData({ ...companyData, representanteLegal: parseInt(e.target.value) })}
                        className="w-full px-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
                      >
                        {partners.map((p, i) => (
                          <option key={i} value={i}>{p.nombre || `Socio ${i + 1}`}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Summary Card */}
            <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/30 rounded-xl p-5">
              <h4 className="font-semibold text-white mb-3 flex items-center gap-2">
                <FileCheck className="w-5 h-5 text-purple-400" />
                Resumen
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-slate-400">Tipo:</span>
                  <span className="text-white ml-2">{COMPANY_TYPES[companyType]?.shortName}</span>
                </div>
                <div>
                  <span className="text-slate-400">Socios:</span>
                  <span className="text-white ml-2">{partners.length}</span>
                </div>
                <div>
                  <span className="text-slate-400">Capital:</span>
                  <span className="text-white ml-2">${parseInt(companyData.capital || 0).toLocaleString('es-CL')}</span>
                </div>
                <div>
                  <span className="text-slate-400">Duración:</span>
                  <span className="text-white ml-2">
                    {companyData.duracion === 'indefinida' ? 'Indefinida' : `${companyData.duracionAnos} años`}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Generated Document */}
        {step === 5 && generatedDocument && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">¡Documento Generado!</h2>
              <p className="text-slate-400">Tu escritura de constitución está lista</p>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white">{generatedDocument.document_name}</h3>
                <div className="flex gap-2">
                  <button
                    onClick={handleCopy}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm flex items-center gap-2 transition-colors"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copiado' : 'Copiar'}
                  </button>
                  <button className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm flex items-center gap-2 transition-colors">
                    <Download className="w-4 h-4" />
                    Descargar PDF
                  </button>
                </div>
              </div>
              <pre className="bg-slate-900 rounded-lg p-4 text-sm text-slate-300 whitespace-pre-wrap overflow-auto max-h-96 font-mono">
                {generatedDocument.content}
              </pre>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5">
              <h4 className="font-semibold text-amber-400 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Próximos Pasos
              </h4>
              <ol className="space-y-2 text-sm text-slate-300 list-decimal list-inside">
                <li>Revisar el documento con un abogado</li>
                <li>Firmar ante notario o con firma electrónica avanzada</li>
                <li>Inscribir en el Registro de Comercio</li>
                <li>Publicar extracto en el Diario Oficial</li>
                <li>Obtener RUT en el SII</li>
                <li>Iniciar actividades</li>
              </ol>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-8 pt-6 border-t border-slate-800">
          {step > 1 && step < 5 && (
            <button
              onClick={() => setStep(step - 1)}
              className="px-6 py-3 bg-slate-800 hover:bg-slate-700 rounded-lg font-medium flex items-center gap-2 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              Anterior
            </button>
          )}
          {step === 1 && <div />}
          
          {step < 4 && (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium flex items-center gap-2 transition-colors ml-auto"
            >
              Siguiente
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
          
          {step === 4 && (
            <button
              onClick={generateDocument}
              disabled={!canProceed() || generating}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium flex items-center gap-2 transition-all ml-auto"
            >
              {generating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Generando...
                </>
              ) : (
                <>
                  <FileText className="w-5 h-5" />
                  Generar Escritura
                </>
              )}
            </button>
          )}

          {step === 5 && (
            <button
              onClick={() => navigate('/escritos-legales')}
              className="px-6 py-3 bg-slate-800 hover:bg-slate-700 rounded-lg font-medium flex items-center gap-2 transition-colors ml-auto"
            >
              Volver a Documentos
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default CompanyConstitutionPage;
